import { transport } from "@/src/config/apiConfig.js";
import { marketingTransport } from "@/src/config/marketingTransport.js";
import { createTransport } from "@/src/config/transportFactory.js";

const { transport: communicationTransport } = createTransport("communication");

export default {
  // ==================== Campaign List ====================
  async getCampaignList(viewId, params) {
    try {
      const res = await transport.get(
        `/api/views/campaigns_in_engage/${viewId}/results`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching campaign list: ", error);
      throw error;
    }
  },

  async createNewCampaign(campaignType) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/new_campaign`,
        {
          campaign_type: campaignType,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating new campaign: ", error);
      throw error;
    }
  },

  async resumeCampaign(campaignId) {
    try {
      const res = await marketingTransport.post(`/marketing/campaigns/resume`, {
        campaign_id: Number(campaignId),
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while resuming campaign: ", error);
      throw error;
    }
  },

  async createCampaignFromAudience(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/create_from_audience`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating campaign from audience: ",
        error,
      );
      throw error;
    }
  },

  async deleteCampaign(campaignId) {
    try {
      const res = await marketingTransport.delete(
        `/marketing/campaigns/${campaignId}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting campaign: ", error);
      throw error;
    }
  },

  async cloneCampaign(campaignId) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/clone_campaign`,
        {
          campaign_id: campaignId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while cloning campaign: ", error);
      throw error;
    }
  },

  async getCampaignMembersCount(
    campaignId,
    includedSegmentIds,
    excludedSegmentIds,
  ) {
    try {
      const params = {
        campaign_id: campaignId,
        included_segment_ids: includedSegmentIds,
        excluded_segment_ids: excludedSegmentIds,
      };
      const res = await marketingTransport.get(
        `/marketing/campaigns/get_campaign_members_count`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign members count: ",
        error,
      );
      throw error;
    }
  },

  async getRecipientsDetails(campaignId, params) {
    try {
      const res = await marketingTransport.get(
        `/marketing/campaigns/get_campaign_members`,
        {
          params: { campaign_id: campaignId, ...params },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching recipients details: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignDetails(campaignId) {
    try {
      const res = await marketingTransport.get(
        `/marketing/campaigns/campaign_details`,
        {
          params: { campaign_id: campaignId },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign details: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignMessageDetails(messageId) {
    try {
      const res = await marketingTransport.get(`/marketing/campaigns/message`, {
        params: { message_id: messageId },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign message details: ",
        error,
      );
      throw error;
    }
  },

  async saveCampaignEmailDetails(payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/save_campaign_email_details`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving campaign email details: ",
        error,
      );
      throw error;
    }
  },

  async saveCampaignHtmlContent(payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/save_campaign_html_content`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving campaign HTML content: ",
        error,
      );
      throw error;
    }
  },

  async saveCampaignWhatsAppDetails(payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/save_campaign_whatsapp_details`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving campaign WhatsApp details: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignHtmlPreviewContent(campaignMessageId) {
    try {
      const res = await marketingTransport.get(
        `/marketing/campaigns/get_campaign_html_preview_content`,
        {
          params: { campaign_message_id: campaignMessageId },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign HTML preview content: ",
        error,
      );
      throw error;
    }
  },

  async scheduleCampaign(payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/schedule_campaign`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while scheduling campaign: ", error);
      throw error;
    }
  },

  async sendTestEmail(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/send_campaign_test_email`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while sending test email: ", error);
      throw error;
    }
  },

  async sendWhatsAppTestMessage(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/send-test/whatsapp`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while sending WhatsApp test message: ", error);
      throw error;
    }
  },

  async getTotalSentCampaigns(params) {
    try {
      const res = await marketingTransport.get(`/marketing/campaigns/total_sent`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching total sent campaigns: ",
        error,
      );
      throw error;
    }
  },

  // ==================== Email Settings ====================
  async getSenderEmails() {
    try {
      const res = await transport.get(`/api/email_settings/sender_emails`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching sender emails: ", error);
      throw error;
    }
  },

  async deleteSenderEmail(senderEmail) {
    try {
      const res = await transport.delete(
        `/api/email_settings/delete_sender_email`,
        {
          params: { sender_email: senderEmail },
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting sender email: ", error);
      throw error;
    }
  },

  async fetchEmailDomains() {
    try {
      const res = await transport.get(`/api/email_settings/fetch_domains`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching email domains: ", error);
      throw error;
    }
  },

  async createEmailDomain(payload) {
    try {
      const res = await transport.post(
        `/api/email_settings/create_domain`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating email domain: ", error);
      throw error;
    }
  },

  async deleteEmailDomain(payload) {
    try {
      const res = await transport.delete(`/api/email_settings/delete_domain`, {
        params: payload,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting email domain: ", error);
      throw error;
    }
  },

  async fetchEmailDomainRecords(sendingDomain) {
    try {
      const res = await transport.get(
        `/api/email_settings/fetch_domain_records`,
        {
          params: { sending_domain: sendingDomain },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching email domain records: ",
        error,
      );
      throw error;
    }
  },

  async saveSenderEmail(payload) {
    try {
      const res = await transport.post(
        `/api/email_settings/sender_email`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while saving sender email: ", error);
      throw error;
    }
  },

  async verifyEmailDomain(sendingDomain) {
    try {
      const res = await transport.get(`/api/email_settings/verify_domain`, {
        params: { sending_domain: sendingDomain },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while verifying email domain: ", error);
      throw error;
    }
  },

  async verifySenderEmail(senderEmail) {
    try {
      const res = await transport.post(
        `/api/email_settings/send_verification_email`,
        senderEmail,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while verifying sender email: ", error);
      throw error;
    }
  },

  async verifyEmailByKey(verificationKey) {
    try {
      const res = await transport.get(
        `/api/email_settings/email_verification`,
        {
          params: { verification_key: verificationKey },
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while verifying email by key: ", error);
      throw error;
    }
  },

  // ==================== Email Templates ====================
  async getCampaignEmailTemplates(params) {
    try {
      const res = await marketingTransport.get(
        `/marketing/email_templates/templates_list`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign email templates: ",
        error,
      );
      throw error;
    }
  },

  async saveCampaignEmailTemplate(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/email_templates/save_email_template`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving campaign email template: ",
        error,
      );
      throw error;
    }
  },

  async deleteCampaignEmailTemplate(params) {
    try {
      const res = await marketingTransport.delete(`/marketing/email_templates`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting campaign email template: ",
        error,
      );
      throw error;
    }
  },

  async fetchExisitngEmailTemplates(params) {
    try {
      const res = await marketingTransport.get(
        `/marketing/email_templates/email_template_details`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching existing email templates: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignEmailTemplateDetails(emailTemplateId) {
    try {
      const res = await marketingTransport.get(
        `/marketing/email_templates/email_template_details`,
        {
          params: { email_template_id: emailTemplateId },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign email template details: ",
        error,
      );
      throw error;
    }
  },

  async saveNewEmailTemplate(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/email_templates/save_email_template`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving new email template: ",
        error,
      );
      throw error;
    }
  },

  async cloneCampaignEmailTemplate(emailTemplateId) {
    try {
      const res = await marketingTransport.post(
        `/marketing/email_templates/clone_email_template`,
        {
          email_template_id: emailTemplateId,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while cloning campaign email template: ",
        error,
      );
      throw error;
    }
  },

  async cloneMessageEmailTemplate(emailTemplateId, campaignMessageId) {
    try {
      const res = await marketingTransport.post(
        `/marketing/email_templates/clone_message_email_template`,
        {
          email_template_id: emailTemplateId,
          campaign_message_id: campaignMessageId,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while cloning message email template: ",
        error,
      );
      throw error;
    }
  },

  async saveMessageEmailTemplate(payload) {
    try {
      const res = await marketingTransport.post(
        `/marketing/email_templates/save_message_email_template`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving message email template: ",
        error,
      );
      throw error;
    }
  },

  // ==================== Campaign Dashboard ====================
  async getCampaignWorkspaceSettings() {
    try {
      const res = await transport.get(
        `/api/dashboard/basic_workspace_settings`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign workspace settings: ",
        error,
      );
      throw error;
    }
  },

  async invalidateCampaignCache() {
    try {
      const res = await transport.get(`/api/dashboard/invalidate_cache`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while invalidating campaign cache: ",
        error,
      );
      throw error;
    }
  },

  // ==================== Campaign Views ====================
  async getAllCampaignViews() {
    try {
      const res = await transport.get(`/api/campaigns_in_engage/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching all campaign views: ",
        error,
      );
      throw error;
    }
  },

  async createCampaignView(payload) {
    try {
      const res = await transport.post(
        `/api/campaigns_in_engage/views`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating campaign view: ", error);
      throw error;
    }
  },

  async getCampaignView(viewId) {
    try {
      const res = await marketingTransport.get(
        `/marketing/campaigns/views/${viewId}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching campaign view: ", error);
      throw error;
    }
  },

  async updateCampaignView(viewId, payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/views/${viewId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating campaign view: ", error);
      throw error;
    }
  },

  async deleteCampaignView(viewId) {
    try {
      const res = await marketingTransport.delete(
        `/marketing/campaigns/views/${viewId}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting campaign view: ", error);
      throw error;
    }
  },

  async updateCurrentCampaignView(viewId) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/views/${viewId}/current_view`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating current campaign view: ",
        error,
      );
      throw error;
    }
  },

  async closeCampaignView(viewId) {
    try {
      const res = await marketingTransport.put(`/marketing/campaigns/views/close`, {
        view_id: viewId,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while closing campaign view: ", error);
      throw error;
    }
  },

  async updateCampaignViewSequence(payload) {
    try {
      const res = await marketingTransport.put(
        `/marketing/campaigns/views/active_views`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating campaign view sequence: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignRecipientsCount(params) {
    try {
      const res = await marketingTransport.post(
        `/marketing/campaigns/get_campaign_members_count`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign recipients count: ",
        error,
      );
      throw error;
    }
  },

  // ==================== Campaign Analytics ====================
  async getCampaignAnalytics(campaignId) {
    try {
      const res = await communicationTransport.get(
        `/communications/analytics/campaigns/${campaignId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign analytics: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignAnalyticsGraph(campaignId, params = {}) {
    try {
      const res = await communicationTransport.get(
        `/communications/analytics/campaigns/${campaignId}/graph`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign analytics graph: ",
        error,
      );
      throw error;
    }
  },

  async getCampaignAudienceStatuses(campaignId, params = {}) {
    try {
      const res = await communicationTransport.get(
        `/communications/profile-messages/campaigns/${campaignId}/audience-status`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching campaign audience statuses: ",
        error,
      );
      throw error;
    }
  },

  // ==================== Automation Email ====================
  async saveAutomationEmailContent(payload) {
    try {
      const res = await transport.put(
        `/api/marketing/automation-email/content`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving automation email content: ",
        error,
      );
      throw error;
    }
  },

  async getAutomationEmailContent(params) {
    try {
      const res = await transport.get(
        `/api/marketing/automation-email/content`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching automation email content: ",
        error,
      );
      throw error;
    }
  },

  async exportCampaignRecipientsCSV(campaignId, payload, exportEmail) {
    try {
      const res = await transport.post(
        `/api/communications/campaigns/${campaignId}/recipients/export-csv?export_email=${encodeURIComponent(exportEmail)}&channel=${encodeURIComponent(payload.channel || "")}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while exporting campaign recipients CSV: ",
        error,
      );
      throw error;
    }
  },

  async bulkExportCampaigns(payload, exportEmail) {
    try {
      const res = await transport.post(
        `/api/communications/campaigns/bulk-export?export_email=${encodeURIComponent(exportEmail)}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while bulk exporting campaigns: ",
        error,
      );
      throw error;
    }
  },
};
